import { routeCarrierAssembly } from "@/setup/routes/mill.routes";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { UseMutationResult } from "@tanstack/react-query";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Carrier } from "../models/Carrier";
import { CarrierActionKey } from "./carrierPolicy";

export type ActionDefinition = {
	label: string;
	testId: string;
	onClick: () => void;
	icon?: ReactNode;
	destructive?: boolean;
	isVisible?: (carrier: Carrier) => boolean;
};

export const useCarrierActions = ({
	carrier,
	setAction,
	updateStatus,
	setOpenInstallUninstallDialog,
	setOpenPassChangeDialog,
	setOpenConfirmDialog,
}: {
	carrier: Carrier | null;
	setAction: (action: Record<string, string>) => void;
	updateStatus: UseMutationResult<
		any,
		Error,
		{
			newStatus: string;
		},
		unknown
	>;
	setOpenInstallUninstallDialog: (open: boolean) => void;
	setOpenPassChangeDialog: (open: boolean) => void;
	setOpenConfirmDialog: (open: boolean) => void;
}) => {
	const navigate = useNavigate();

	const actions: Record<CarrierActionKey, ActionDefinition> = {
		signOff: {
			label: "Sign off",
			testId: "sign-off-button",
			onClick: () =>
				navigate(
					`/${routeCarrierAssembly}/${carrier?.PartId}/build/${carrier?.CarrierInstanceId}`,
				),
		},
		newBuild: {
			label: "New build",
			testId: "new-build-button",
			onClick: () => {
				setAction({ action: "build", code: "A", message: "built" });
				updateStatus.mutate({ newStatus: "A" });
			},
		},
		install: {
			label: "Install",
			testId: "install-button",
			onClick: () => {
				setAction({ action: "install", code: "I", message: "installed" });
				setOpenInstallUninstallDialog(true);
			},
		},
		passChange: {
			label: "Pass change",
			testId: "pass-change-button",
			icon: <SwapHorizIcon />,
			onClick: () => setOpenPassChangeDialog(true),
			isVisible: (c) =>
				!c.TopRollName?.includes("UR") || !c.BottomRollName?.includes("UR"),
		},
		uninstall: {
			label: "Uninstall",
			testId: "uninstall-button",
			onClick: () => {
				setAction({ action: "uninstall", code: "U", message: "uninstalled" });
				setOpenInstallUninstallDialog(true);
			},
		},
		edit: {
			label: "Edit current build",
			testId: "edit-current-build-button",
			icon: <EditIcon />,
			onClick: () =>
				navigate(
					`/${routeCarrierAssembly}/${carrier?.PartId}/build/${carrier?.CarrierInstanceId}`,
				),
		},
		restart: {
			label: "Restart build",
			testId: "restart-build-button",
			icon: <RefreshIcon />,
			destructive: true,
			onClick: () => {
				setAction({ action: "restart", code: "A", message: "restarted" });
				setOpenConfirmDialog(true);
			},
		},
		disassemble: {
			label: "Disassemble build",
			testId: "disassemble-build-button",
			icon: <RefreshIcon />,
			onClick: () => {
				setAction({
					action: "disassemble",
					code: "F",
					message: "disassembled",
				});
				setOpenConfirmDialog(true);
			},
		},
	};

	return { actions };
};
